import { HsaIntentSource } from "@redotech/cockroachdb-codegen/tables/conversion/hsa-intent-source";
import { HsaFunnelStepKind } from "@redotech/redo-conversion-hsa-common/hsa-flow-step-order";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { hsaFunnelTotals } from "./get-hsa-funnel-analytics";

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD");

/**
 * One merchant's LMN questionnaire funnel: totals, knockouts by reason,
 * partner rejections by code, and the questionnaire step by step in the
 * merchant's active flow order (reached / dropped / knocked out / still on it
 * / routed past it), which is what the questionnaire flow chart draws.
 */
export const getHsaFunnelAnalyticsForTeam = {
  name: "getHsaFunnelAnalyticsForTeam" as const,
  input: z.object({
    teamId: zExt.objectId(),
    startDate: isoDate,
    endDate: isoDate,
  }),
  output: z.object({
    /** The merchant, for the page title and breadcrumb. */
    team: z.object({ name: z.string(), storeUrl: z.string() }),
    totals: hsaFunnelTotals,
    knockoutsByReason: z.array(
      z.object({ reason: z.string(), count: z.number() }),
    ),
    rejectionsByPartnerCode: z.array(
      z.object({ code: z.string(), count: z.number() }),
    ),
    steps: z.array(
      z.object({
        handle: z.string(),
        label: z.string(),
        kind: createEnum(HsaFunnelStepKind),
        sessionsReached: z.number(),
        droppedHere: z.number(),
        knockedOutHere: z.number(),
        /** Sessions still sitting on this step. */
        inProgressHere: z.number(),
        /** Shoppers the flow routed past this step. */
        skipped: z.number(),
      }),
    ),
    /**
     * Storefront elements → HSA/FSA attach, per element: signals recorded in
     * range, orders whose cart carried that element's intent, and how many of
     * those paid with HSA/FSA.
     */
    intentAttach: z.object({
      sources: z.array(
        z.object({
          source: createEnum(HsaIntentSource),
          signals: z.number(),
          orders: z.number(),
          hsaPaidOrders: z.number(),
          /** hsaPaidOrders / orders; null with no orders. */
          attachRate: z.number().nullable(),
        }),
      ),
      /** HSA/FSA-paid orders in range that carried no storefront intent. */
      ordersPaidWithHsaWithoutIntent: z.number(),
    }),
    /** Earliest date with funnel data; the picker clamps to it. */
    dataStartDate: isoDate,
  }),
};
